import * as React from 'react';

import { createEmptyDependantFormValues } from './dependants/types';

import type { IDependantFormValues } from './dependants/types';

export type DependantCount = 1 | 2;

/**
 * One slot in the loved-ones flow. `serverId` / `caregiverRelationshipId` are
 * set when the slot was seeded from existing server data (resume of a
 * previously-completed flow); for fresh entries they stay null until the
 * review step submits and creates the rows.
 */
export interface IPendingDependant {
  values: IDependantFormValues;
  serverId: string | null;
  caregiverRelationshipId: string | null;
}

export const createEmptyPendingDependant = (): IPendingDependant => ({
  values: createEmptyDependantFormValues(),
  serverId: null,
  caregiverRelationshipId: null,
});

interface IOnboardingContextValue {
  /**
   * The E.164 phone number the user previously granted SMS consent for, or
   * `null` if the user hasn't consented in this session. `PhoneNumberEntryStep`
   * derives the checkbox checked state by comparing this against the current
   * input — same number restores as checked, different number clears it.
   */
  consentedPhoneE164: string | null;
  setConsentedPhoneE164: (next: string | null) => void;
  /**
   * Loved-ones count, derived from `pendingDependants.length` — the count step
   * sizes the array to the chosen count (padding empty slots), so the array
   * length *is* the count. `null` until the first slot exists. Read-only:
   * change the count by sizing `pendingDependants`, not by setting this.
   */
  dependantCount: DependantCount | null;
  /**
   * Per-slot form state for the loved-ones flow. The count step sizes this
   * array, the entry step writes into it, and the review step is the single
   * point that persists it — creating slots without `serverId` and updating
   * slots that have one. Survives the entry ↔ review round-trip so an Edit
   * from review doesn't lose the user's typing.
   */
  pendingDependants: IPendingDependant[];
  setPendingDependants: React.Dispatch<React.SetStateAction<IPendingDependant[]>>;
  /**
   * Dependant ids whose slots were dropped from `pendingDependants` (typically
   * because the user shrank the count via `DependantCountStep`) but still
   * exist on the server. The review step deletes these before persisting the
   * remaining slots so a count change doesn't orphan a dependant row.
   */
  pendingDependantDeletions: string[];
  setPendingDependantDeletions: React.Dispatch<React.SetStateAction<string[]>>;
  /**
   * The slot `DependantEntryStep` should open on, set only by
   * `DependantReviewStep`'s Edit-on-card so the user lands on that dependant.
   * Every other way into the entry step (forward from count, or backing in
   * from review) leaves this null and opens on slot 0. The entry step reads it
   * once on mount and clears it.
   */
  editingDependantIndex: number | null;
  setEditingDependantIndex: (next: number | null) => void;
}

const OnboardingContext = React.createContext<IOnboardingContextValue>({
  consentedPhoneE164: null,
  setConsentedPhoneE164: () => {},
  dependantCount: null,
  pendingDependants: [],
  setPendingDependants: () => {},
  pendingDependantDeletions: [],
  setPendingDependantDeletions: () => {},
  editingDependantIndex: null,
  setEditingDependantIndex: () => {},
});

/**
 * Holds cross-mount state for the onboarding wizard — everything that has
 * to survive a back-nav round-trip and isn't a derivation of server data.
 * Today: SMS-consent target phone (for the entry step's checkbox), the
 * loved-ones count (entry → review), the per-slot dependant form state
 * (`pendingDependants`) that the review step persists in one shot, the
 * `pendingDependantDeletions` queue for dropped slots, and the entry-step
 * opening slot (`editingDependantIndex`, set by review's Edit-on-card).
 *
 * The phone number itself flows through Apollo, not this provider. Dependant
 * rows are persisted only at the review step, so the in-flight form values
 * live here until then. Only add a slot here when the state genuinely can't
 * be expressed as a server-data derivation.
 *
 * Scoped to `BaseOnboardingFlow`'s render tree — unmounts with the wizard.
 *
 * `initialPendingDependants` seeds the loved-ones slots at mount. The flow's
 * Apollo-connected entry point computes them from the user's persisted
 * dependants (already-resolved, so the seed is synchronous) and passes them
 * in — that's what makes the entry/review forms populated no matter how
 * they're reached (forward, resume, or back-nav), without the provider itself
 * having to fetch. `dependantCount` is derived from the seeded array's length.
 */
export const OnboardingProvider = ({
  children,
  initialPendingDependants = [],
}: {
  children: React.ReactNode;
  initialPendingDependants?: IPendingDependant[];
}) => {
  const [consentedPhoneE164, setConsentedPhoneE164] = React.useState<string | null>(null);
  const [pendingDependants, setPendingDependants] = React.useState<IPendingDependant[]>(initialPendingDependants);
  const [pendingDependantDeletions, setPendingDependantDeletions] = React.useState<string[]>([]);
  const [editingDependantIndex, setEditingDependantIndex] = React.useState<number | null>(null);
  // The count step sizes `pendingDependants` to the chosen count, so the array
  // length is the count. Clamp to the 2-person family cap defensively.
  const dependantCount: DependantCount | null =
    pendingDependants.length >= 2 ? 2 : pendingDependants.length === 1 ? 1 : null;
  const value = React.useMemo(
    () => ({
      consentedPhoneE164,
      setConsentedPhoneE164,
      dependantCount,
      pendingDependants,
      setPendingDependants,
      pendingDependantDeletions,
      setPendingDependantDeletions,
      editingDependantIndex,
      setEditingDependantIndex,
    }),
    [consentedPhoneE164, dependantCount, pendingDependants, pendingDependantDeletions, editingDependantIndex]
  );
  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};

export const useOnboardingContext = (): IOnboardingContextValue => {
  return React.useContext(OnboardingContext);
};
